import React from 'react';
import { X, Eye, EyeOff, CheckSquare, Square, Palette, Grid3X3, Rows } from 'lucide-react';

export type ColorMode =
    | 'none'
    | 'risk-traffic-light'
    | 'alt-gray'
    | 'alt-blue'
    | 'alt-green-white';

export interface LayoutSettings {
    wrapText: boolean;
    colorMode: ColorMode;
    showCellBorders: boolean;
    hiddenColumns: Set<string>;
    riskRanges: {
        red: { min: number; max: number };
        yellow: { min: number; max: number };
        green: { min: number; max: number };
    };
}

export const DEFAULT_LAYOUT_SETTINGS: LayoutSettings = {
    wrapText: true,
    colorMode: 'risk-traffic-light',
    showCellBorders: false,
    hiddenColumns: new Set(),
    riskRanges: {
        red: { min: 7, max: 10 },
        yellow: { min: 4, max: 6 },
        green: { min: 1, max: 3 },
    },
};

interface LayoutSettingsPanelProps {
    settings: LayoutSettings;
    onSettingsChange: (settings: LayoutSettings) => void;
    onClose: () => void;
    availableColumns: Array<{ key: string; header: string }>;
}

const colorModeOptions: Array<{ id: ColorMode; label: string; preview: React.ReactNode }> = [
    {
        id: 'none',
        label: 'No Color',
        preview: (
            <div className="flex flex-col gap-0.5">
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
            </div>
        ),
    },
    {
        id: 'risk-traffic-light',
        label: 'Traffic Light (Risk)',
        preview: (
            <div className="flex flex-col gap-0.5">
                <div className="h-3 w-full bg-red-100 border border-red-200 rounded-sm" />
                <div className="h-3 w-full bg-yellow-100 border border-yellow-200 rounded-sm" />
                <div className="h-3 w-full bg-green-100 border border-green-200 rounded-sm" />
            </div>
        ),
    },
    {
        id: 'alt-gray',
        label: 'Alternating Gray',
        preview: (
            <div className="flex flex-col gap-0.5">
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
                <div className="h-3 w-full bg-slate-100 border border-slate-200 rounded-sm" />
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
            </div>
        ),
    },
    {
        id: 'alt-blue',
        label: 'Alternating Blue',
        preview: (
            <div className="flex flex-col gap-0.5">
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
                <div className="h-3 w-full bg-blue-50 border border-blue-100 rounded-sm" />
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
            </div>
        ),
    },
    {
        id: 'alt-green-white',
        label: 'Alternating Green',
        preview: (
            <div className="flex flex-col gap-0.5">
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
                <div className="h-3 w-full bg-emerald-50 border border-emerald-100 rounded-sm" />
                <div className="h-3 w-full bg-white border border-slate-200 rounded-sm" />
            </div>
        ),
    },
];

export const LayoutSettingsPanel: React.FC<LayoutSettingsPanelProps> = ({
    settings,
    onSettingsChange,
    onClose,
    availableColumns,
}) => {
    const update = (partial: Partial<LayoutSettings>) => {
        onSettingsChange({ ...settings, ...partial });
    };

    const toggleColumn = (key: string) => {
        const next = new Set(settings.hiddenColumns);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        update({ hiddenColumns: next });
    };

    const showAll = () => update({ hiddenColumns: new Set() });

    return (
        <div className="fixed inset-0 z-[200] flex justify-end" onClick={onClose}>
            <div
                className="w-[380px] bg-card border-l border-border h-full overflow-y-auto shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-border bg-gradient-to-r from-slate-50 dark:from-slate-900 to-transparent shrink-0">
                    <div>
                        <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
                            <Grid3X3 size={18} className="text-primary" />
                            Layout Settings
                        </h2>
                        <p className="text-xs text-text-secondary mt-0.5">Customize your FMEA editor view</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-7">

                    {/* === TEXT WRAPPING === */}
                    <section>
                        <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-2">
                            <Rows size={14} /> Text Display
                        </h3>
                        <div className="flex items-center justify-between p-3 bg-background rounded-xl border border-border">
                            <div>
                                <p className="text-sm font-semibold text-text-primary">Wrap Text</p>
                                <p className="text-xs text-text-secondary">Show full text in cells</p>
                            </div>
                            <button
                                onClick={() => update({ wrapText: !settings.wrapText })}
                                className={`relative w-12 h-6 rounded-full transition-colors ${settings.wrapText ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                            >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.wrapText ? 'translate-x-6' : 'translate-x-0'}`} />
                            </button>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-background rounded-xl border border-border mt-2">
                            <div>
                                <p className="text-sm font-semibold text-text-primary">Cell Borders</p>
                                <p className="text-xs text-text-secondary">Show borders on all cells</p>
                            </div>
                            <button
                                onClick={() => update({ showCellBorders: !settings.showCellBorders })}
                                className={`relative w-12 h-6 rounded-full transition-colors ${settings.showCellBorders ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                            >
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${settings.showCellBorders ? 'translate-x-6' : 'translate-x-0'}`} />
                            </button>
                        </div>
                    </section>

                    {/* === COLOR MODE === */}
                    <section>
                        <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-2">
                            <Palette size={14} /> Row Color Mode
                        </h3>
                        <div className="grid grid-cols-1 gap-2">
                            {colorModeOptions.map(opt => (
                                <button
                                    key={opt.id}
                                    onClick={() => update({ colorMode: opt.id })}
                                    className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${settings.colorMode === opt.id
                                        ? 'border-primary bg-primary/5 shadow-sm'
                                        : 'border-border hover:border-primary/40 bg-background'
                                        }`}
                                >
                                    <div className="w-14 shrink-0">{opt.preview}</div>
                                    <span className={`text-sm font-semibold ${settings.colorMode === opt.id ? 'text-primary' : 'text-text-primary'}`}>
                                        {opt.label}
                                    </span>
                                    {settings.colorMode === opt.id && (
                                        <CheckSquare size={16} className="ml-auto text-primary shrink-0" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </section>

                    {/* === TRAFFIC LIGHT RANGES === */}
                    {settings.colorMode === 'risk-traffic-light' && (
                        <section>
                            <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary mb-3">
                                🚦 Traffic Light Thresholds (S, O, D Rating)
                            </h3>
                            <p className="text-xs text-text-secondary mb-3">Set the min/max rating values (1–10) for each color band.</p>
                            {(['red', 'yellow', 'green'] as const).map(color => {
                                const labels = { red: '🔴 Red (High Risk)', yellow: '🟡 Yellow (Medium)', green: '🟢 Green (Low)' };
                                const bgClass = { red: 'bg-red-50 border-red-200', yellow: 'bg-yellow-50 border-yellow-200', green: 'bg-green-50 border-green-200' };
                                return (
                                    <div key={color} className={`p-3 rounded-xl border ${bgClass[color]} mb-2`}>
                                        <p className="text-xs font-bold mb-2">{labels[color]}</p>
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs text-text-secondary w-8">Min</label>
                                            <input
                                                type="number" min={1} max={10}
                                                value={settings.riskRanges[color].min}
                                                onChange={e => update({
                                                    riskRanges: {
                                                        ...settings.riskRanges,
                                                        [color]: { ...settings.riskRanges[color], min: Number(e.target.value) }
                                                    }
                                                })}
                                                className="w-16 p-1 rounded border border-border text-center text-sm bg-white"
                                            />
                                            <label className="text-xs text-text-secondary w-8">Max</label>
                                            <input
                                                type="number" min={1} max={10}
                                                value={settings.riskRanges[color].max}
                                                onChange={e => update({
                                                    riskRanges: {
                                                        ...settings.riskRanges,
                                                        [color]: { ...settings.riskRanges[color], max: Number(e.target.value) }
                                                    }
                                                })}
                                                className="w-16 p-1 rounded border border-border text-center text-sm bg-white"
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </section>
                    )}

                    {/* === COLUMN VISIBILITY === */}
                    <section>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary flex items-center gap-2">
                                <Eye size={14} /> Column Visibility
                            </h3>
                            <button onClick={showAll} className="text-xs text-primary hover:underline font-semibold">Show All</button>
                        </div>
                        <div className="space-y-1">
                            {availableColumns.map(col => {
                                const isHidden = settings.hiddenColumns.has(col.key);
                                return (
                                    <button
                                        key={col.key}
                                        onClick={() => toggleColumn(col.key)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${isHidden
                                            ? 'bg-slate-50 dark:bg-slate-900/50 border-border/50 opacity-60'
                                            : 'bg-background border-border hover:border-primary/40'
                                            }`}
                                    >
                                        {isHidden
                                            ? <EyeOff size={14} className="text-slate-400 shrink-0" />
                                            : <Eye size={14} className="text-emerald-500 shrink-0" />
                                        }
                                        <span className={`text-sm font-medium ${isHidden ? 'line-through text-text-secondary' : 'text-text-primary'}`}>
                                            {col.header}
                                        </span>
                                        <span className="ml-auto text-[10px] font-mono text-text-secondary">{col.key}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border shrink-0">
                    <button
                        onClick={() => onSettingsChange(DEFAULT_LAYOUT_SETTINGS)}
                        className="w-full py-2 text-sm text-text-secondary hover:text-text-primary border border-border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                        Reset to Defaults
                    </button>
                </div>
            </div>
        </div>
    );
};
